import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DataPage.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, "Open Sans", sans-serif',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#374151',
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: 0,
  },
  phoneSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#1d4ed8',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '20px',
    flexShrink: 0,
  },
  tabs: {
    display: 'flex',
    gap: '0',
    marginBottom: '20px',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: '#6b7280',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.15s',
  },
  tabActive: {
    color: '#1d4ed8',
    borderBottomColor: '#1d4ed8',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  formGroupFull: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
  },
  textarea: {
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: 'none',
    background: '#1d4ed8',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    borderRadius: '14px',
    fontSize: '12px',
    fontWeight: 600,
    marginRight: '6px',
    marginBottom: '6px',
  },
  tagRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: 1,
    padding: '0 2px',
    opacity: 0.6,
  },
  tagAddRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  selectSmall: {
    padding: '6px 8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: '#fff',
  },
  btnSmall: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  mapContainer: {
    height: '320px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    marginTop: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#fafafa',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
  },
  msgItem: {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  newTagRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginTop: '8px',
  },
  colorInput: {
    width: '32px',
    height: '28px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '1px',
    cursor: 'pointer',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  statCard: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '12px 16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1a1a2e',
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
  },
};

function formatPhone(phone) {
  if (!phone) return '';
  return phone.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3');
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Map click handler component
function MapClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

export default function WhatsAppContatoDetalhes() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef(null);

  const [contato, setContato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('info');

  // Form fields
  const [form, setForm] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3b82f6');
  const [showNewTag, setShowNewTag] = useState(false);
  const [showManageTags, setShowManageTags] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('#3b82f6');

  // Conversas tab
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Atendimentos tab
  const [atendimentos, setAtendimentos] = useState([]);
  const [loadingAtendimentos, setLoadingAtendimentos] = useState(false);

  // Sistema tab
  const [sistema, setSistema] = useState(null);
  const [loadingSistema, setLoadingSistema] = useState(false);

  // Map position
  const [mapPos, setMapPos] = useState(null);

  const fetchContato = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/contatos/${encodeURIComponent(phone)}`);
      setContato(data);
      setForm({
        nome: data.nome || '',
        email: data.email || '',
        cpf: data.cpf || '',
        observacao: data.observacao || '',
        endereco: data.endereco || '',
        bairro: data.bairro || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        cep: data.cep || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
      });
      if (data.latitude && data.longitude) {
        setMapPos({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
      }
    } catch (err) {
      console.error('Erro ao buscar contato:', err);
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/contatos/tags/all`);
      setAllTags(data || []);
    } catch (err) {
      console.error('Erro ao buscar tags:', err);
    }
  }, []);

  const fetchTimeline = useCallback(async () => {
    setLoadingTimeline(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/conversas-unificadas/${encodeURIComponent(phone)}`);
      setTimeline(Array.isArray(data) ? data : data.timeline || []);
    } catch (err) {
      console.error('Erro ao buscar timeline:', err);
    } finally {
      setLoadingTimeline(false);
    }
  }, [phone]);

  const fetchAtendimentos = useCallback(async () => {
    setLoadingAtendimentos(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/atendimentos/contato/${encodeURIComponent(phone)}`);
      setAtendimentos(data.atendimentos || data || []);
    } catch (err) {
      console.error('Erro ao buscar atendimentos:', err);
    } finally {
      setLoadingAtendimentos(false);
    }
  }, [phone]);

  const fetchSistema = useCallback(async () => {
    setLoadingSistema(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/contato/${encodeURIComponent(phone)}`);
      setSistema(data);
    } catch (err) {
      console.error('Erro ao buscar dados do sistema:', err);
    } finally {
      setLoadingSistema(false);
    }
  }, [phone]);

  useEffect(() => { fetchContato(); fetchTags(); }, [fetchContato, fetchTags]);
  useEffect(() => { if (tab === 'conversas') fetchTimeline(); }, [tab, fetchTimeline]);
  useEffect(() => { if (tab === 'atendimentos') fetchAtendimentos(); }, [tab, fetchAtendimentos]);
  useEffect(() => { if (tab === 'sistema') fetchSistema(); }, [tab, fetchSistema]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/whatsapp/contatos/${encodeURIComponent(phone)}`, {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      });
      await fetchContato();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      alert('Erro ao salvar contato');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = async () => {
    if (!selectedTagToAdd) return;
    try {
      await axios.post(`${API_URL}/whatsapp/contatos/${encodeURIComponent(phone)}/tags`, { tagId: parseInt(selectedTagToAdd) });
      setSelectedTagToAdd('');
      await fetchContato();
    } catch (err) {
      console.error('Erro ao adicionar tag:', err);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await axios.delete(`${API_URL}/whatsapp/contatos/${encodeURIComponent(phone)}/tags/${tagId}`);
      await fetchContato();
    } catch (err) {
      console.error('Erro ao remover tag:', err);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      await axios.post(`${API_URL}/whatsapp/contatos/tags`, { tag: newTagName.trim(), cor: newTagColor });
      setNewTagName('');
      setShowNewTag(false);
      await fetchTags();
    } catch (err) {
      console.error('Erro ao criar tag:', err);
      if (err.response?.status === 409) alert('Tag já existe');
    }
  };

  const handleEditTag = async (id) => {
    if (!editTagName.trim()) return;
    try {
      await axios.put(`${API_URL}/whatsapp/contatos/tags/${id}`, { tag: editTagName.trim(), cor: editTagColor });
      setEditingTag(null);
      await fetchTags();
      await fetchContato();
    } catch (err) {
      console.error('Erro ao editar tag:', err);
      if (err.response?.status === 409) alert('Tag já existe com esse nome');
    }
  };

  const handleDeleteTag = async (id, tagName) => {
    if (!window.confirm(`Deletar a tag "${tagName}"? Será removida de todos os contatos.`)) return;
    try {
      await axios.delete(`${API_URL}/whatsapp/contatos/tags/${id}`);
      await fetchTags();
      await fetchContato();
    } catch (err) {
      console.error('Erro ao deletar tag:', err);
    }
  };

  const handleMapClick = (latlng) => {
    setMapPos(latlng);
    setForm(f => ({ ...f, latitude: latlng.lat.toFixed(7), longitude: latlng.lng.toFixed(7) }));
  };

  const handleGeocode = async () => {
    const addr = [form.endereco, form.bairro, form.cidade, form.estado, form.cep].filter(Boolean).join(', ');
    if (!addr) return;
    try {
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`);
      const results = await resp.json();
      if (results.length > 0) {
        const pos = { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
        setMapPos(pos);
        setForm(f => ({ ...f, latitude: pos.lat.toFixed(7), longitude: pos.lng.toFixed(7) }));
        if (mapRef.current) mapRef.current.flyTo(pos, 16);
      } else {
        alert('Endereço não encontrado');
      }
    } catch (err) {
      console.error('Erro ao geocodificar:', err);
    }
  };

  if (loading) return <div style={styles.container} className="data-page"><div style={styles.empty}>Carregando...</div></div>;
  if (!contato) return <div style={styles.container} className="data-page"><div style={styles.empty}>Contato não encontrado</div></div>;

  const contatoTags = Array.isArray(contato.tags) ? contato.tags : [];
  const availableTags = allTags.filter(t => !contatoTags.some(ct => ct.id === t.id));

  return (
    <div style={styles.container} className="data-page">
      {/* Header */}
      <div style={styles.headerRow}>
        <button style={styles.backBtn} onClick={() => navigate('/whatsapp-contatos')}>&#8592; Voltar</button>
        <div style={styles.avatar}>{(contato.nome || contato.phone || '?')[0].toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.pageTitle}>{contato.nome || 'Sem nome'}</h1>
          <p style={styles.phoneSubtitle}>{formatPhone(contato.phone)}</p>
        </div>
        <button
          style={{ ...styles.backBtn, background: '#059669', color: '#fff', border: 'none', fontWeight: 600 }}
          onClick={() => navigate(`/whatsapp-conversas/${encodeURIComponent(phone)}`)}
        >
          Abrir conversa
        </button>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{contato.stats?.total_recebidas || 0}</div>
          <div style={styles.statLabel}>Recebidas</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{contato.stats?.total_enviadas || 0}</div>
          <div style={styles.statLabel}>Enviadas</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{formatDateTime(contato.stats?.ultima_recebida)}</div>
          <div style={styles.statLabel}>Última recebida</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{formatDateTime(contato.stats?.ultimo_envio)}</div>
          <div style={styles.statLabel}>Último envio</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['info', 'sistema', 'conversas', 'atendimentos'].map(t => (
          <button
            key={t}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            onClick={() => setTab(t)}
          >
            {{ info: 'Informações', sistema: 'Sistema', conversas: 'Conversas', atendimentos: 'Atendimentos' }[t]}
          </button>
        ))}
      </div>

      {/* ─── TAB: Informações ─── */}
      {tab === 'info' && (
        <>
          {/* Dados básicos */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Dados do contato</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome</label>
                <input style={styles.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>CPF</label>
                <input style={styles.input} value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Telefone</label>
                <input style={styles.input} value={formatPhone(contato.phone)} disabled />
              </div>
              <div style={styles.formGroupFull}>
                <label style={styles.label}>Observação</label>
                <textarea style={styles.textarea} value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Tags</div>
            <div>
              {contatoTags.map(t => (
                <span key={t.id} style={{ ...styles.tag, backgroundColor: t.cor + '20', color: t.cor }}>
                  {t.tag}
                  <button style={{ ...styles.tagRemove, color: t.cor }} onClick={() => handleRemoveTag(t.id)}>&times;</button>
                </span>
              ))}
              {contatoTags.length === 0 && <span style={{ fontSize: '13px', color: '#9ca3af' }}>Nenhuma tag</span>}
            </div>
            <div style={styles.tagAddRow}>
              {availableTags.length > 0 && (
                <>
                  <select style={styles.selectSmall} value={selectedTagToAdd} onChange={e => setSelectedTagToAdd(e.target.value)}>
                    <option value="">Adicionar tag...</option>
                    {availableTags.map(t => <option key={t.id} value={t.id}>{t.tag}</option>)}
                  </select>
                  <button style={{ ...styles.btnSmall, background: '#1d4ed8', color: '#fff' }} onClick={handleAddTag}>Adicionar</button>
                </>
              )}
              <button style={{ ...styles.btnSmall, background: '#f3f4f6', color: '#374151' }} onClick={() => setShowNewTag(!showNewTag)}>
                {showNewTag ? 'Cancelar' : '+ Nova tag'}
              </button>
            </div>
            {showNewTag && (
              <div style={styles.newTagRow}>
                <input
                  style={{ ...styles.input, fontSize: '12px', padding: '5px 8px', minWidth: '120px' }}
                  placeholder="Nome da tag"
                  value={newTagName}
                  onChange={e => setNewTagName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateTag()}
                />
                <input type="color" style={styles.colorInput} value={newTagColor} onChange={e => setNewTagColor(e.target.value)} />
                <button style={{ ...styles.btnSmall, background: '#059669', color: '#fff' }} onClick={handleCreateTag}>Criar</button>
              </div>
            )}
            <div style={{ marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '10px' }}>
              <button
                style={{ ...styles.btnSmall, background: 'none', color: '#6b7280', padding: '4px 0', fontSize: '11px' }}
                onClick={() => setShowManageTags(!showManageTags)}
              >
                {showManageTags ? 'Ocultar gerenciamento' : 'Gerenciar tags existentes'}
              </button>
              {showManageTags && (
                <div style={{ marginTop: '8px' }}>
                  {allTags.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>Nenhuma tag cadastrada</div>
                  ) : allTags.map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid #f9fafb' }}>
                      {editingTag === t.id ? (
                        <>
                          <input
                            style={{ ...styles.input, fontSize: '12px', padding: '4px 6px', flex: 1, minWidth: '80px' }}
                            value={editTagName}
                            onChange={e => setEditTagName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleEditTag(t.id)}
                          />
                          <input type="color" style={styles.colorInput} value={editTagColor} onChange={e => setEditTagColor(e.target.value)} />
                          <button style={{ ...styles.btnSmall, background: '#059669', color: '#fff', padding: '4px 8px' }} onClick={() => handleEditTag(t.id)}>Salvar</button>
                          <button style={{ ...styles.btnSmall, background: '#f3f4f6', color: '#374151', padding: '4px 8px' }} onClick={() => setEditingTag(null)}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <span style={{ ...styles.tag, backgroundColor: t.cor + '20', color: t.cor, margin: 0 }}>{t.tag}</span>
                          <div style={{ flex: 1 }} />
                          <button
                            style={{ ...styles.btnSmall, background: 'none', color: '#6b7280', padding: '2px 6px', fontSize: '11px' }}
                            onClick={() => { setEditingTag(t.id); setEditTagName(t.tag); setEditTagColor(t.cor); }}
                          >
                            Editar
                          </button>
                          <button
                            style={{ ...styles.btnSmall, background: 'none', color: '#ef4444', padding: '2px 6px', fontSize: '11px' }}
                            onClick={() => handleDeleteTag(t.id, t.tag)}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Endereço */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Endereço</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroupFull}>
                <label style={styles.label}>Endereço</label>
                <input style={styles.input} value={form.endereco} onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bairro</label>
                <input style={styles.input} value={form.bairro} onChange={e => setForm(f => ({ ...f, bairro: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cidade</label>
                <input style={styles.input} value={form.cidade} onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Estado</label>
                <input style={styles.input} value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} maxLength={2} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>CEP</label>
                <input style={styles.input} value={form.cep} onChange={e => setForm(f => ({ ...f, cep: e.target.value }))} />
              </div>
            </div>

            {/* Mapa */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={styles.label}>Localização no mapa (clique para marcar)</span>
                <button
                  style={{ ...styles.btnSmall, background: '#1d4ed8', color: '#fff' }}
                  onClick={handleGeocode}
                >
                  Localizar endereço
                </button>
              </div>
              <div style={styles.mapContainer}>
                <MapContainer
                  center={mapPos || { lat: -20.3, lng: -45.5 }}
                  zoom={mapPos ? 16 : 8}
                  style={{ height: '100%', width: '100%' }}
                  ref={mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  <MapClickHandler onClick={handleMapClick} />
                  {mapPos && <Marker position={mapPos} />}
                </MapContainer>
              </div>
              {form.latitude && form.longitude && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  Lat: {form.latitude}, Lng: {form.longitude}
                </div>
              )}
            </div>
          </div>

          {/* Paciente vinculado */}
          {contato.paciente && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>Paciente vinculado</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nome</label>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{contato.paciente.NOME}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>CPF</label>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{contato.paciente.CPF || '—'}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nascimento</label>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{contato.paciente.DATA_NASCIMENTO ? new Date(contato.paciente.DATA_NASCIMENTO).toLocaleDateString('pt-BR') : '—'}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Celular</label>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{contato.paciente.TELEFONE_CELULAR || '—'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Botão salvar */}
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </>
      )}

      {/* ─── TAB: Sistema ─── */}
      {tab === 'sistema' && (
        <>
          {loadingSistema ? (
            <div style={styles.empty}>Carregando dados do sistema...</div>
          ) : !sistema ? (
            <div style={styles.empty}>Não foi possível carregar dados do sistema</div>
          ) : (
            <>
              {/* Paciente vinculado */}
              {sistema.paciente && (
                <div style={styles.card}>
                  <div style={styles.cardTitle}>Paciente vinculado</div>
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Código</label>
                      <div style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>{sistema.paciente.ID}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Nome</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.NOME}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>CPF</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.CPF || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Nascimento</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.DATA_NASCIMENTO ? new Date(sistema.paciente.DATA_NASCIMENTO).toLocaleDateString('pt-BR') : '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Sexo</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.SEXO || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Celular</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.TELEFONE_CELULAR || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Telefone</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.TELEFONE || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>WhatsApp</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.WHATSAPP || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Email</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.paciente.EMAIL || '—'}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Endereço</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{[sistema.paciente.ENDERECO, sistema.paciente.BAIRRO, sistema.paciente.CIDADE].filter(Boolean).join(', ') || '—'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pacientes encontrados por telefone (fuzzy match) */}
              {!sistema.paciente && sistema.pacientes_telefone && sistema.pacientes_telefone.length > 0 && (
                <div style={styles.card}>
                  <div style={styles.cardTitle}>Pacientes encontrados por telefone</div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Nenhum paciente vinculado. Encontrados {sistema.pacientes_telefone.length} paciente(s) com telefone semelhante:
                  </p>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Código</th>
                        <th style={styles.th}>Nome</th>
                        <th style={styles.th}>CPF</th>
                        <th style={styles.th}>Celular</th>
                        <th style={styles.th}>Nascimento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sistema.pacientes_telefone.map((p, i) => (
                        <tr key={p.ID || i}>
                          <td style={styles.td}>{p.ID}</td>
                          <td style={styles.td}>{p.NOME}</td>
                          <td style={styles.td}>{p.CPF || '—'}</td>
                          <td style={styles.td}>{p.TELEFONE_CELULAR || '—'}</td>
                          <td style={styles.td}>{p.DATA_NASCIMENTO ? new Date(p.DATA_NASCIMENTO).toLocaleDateString('pt-BR') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Nenhum paciente */}
              {!sistema.paciente && (!sistema.pacientes_telefone || sistema.pacientes_telefone.length === 0) && (
                <div style={styles.card}>
                  <div style={styles.empty}>Nenhum paciente encontrado no sistema para este telefone</div>
                </div>
              )}

              {/* Últimas solicitações (se tem paciente) */}
              {sistema.protocolos && sistema.protocolos.length > 0 && (
                <div style={styles.card}>
                  <div style={styles.cardTitle}>Últimos protocolos</div>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Protocolo</th>
                        <th style={styles.th}>Data</th>
                        <th style={styles.th}>Local</th>
                        <th style={styles.th}>Convênio</th>
                        <th style={styles.th}>Exames</th>
                        <th style={styles.th}>Liberado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sistema.protocolos.map((p, i) => {
                        const prontos = p.EXAMES_PRONTOS || 0;
                        const total = p.TOTAL_EXAMES || 0;
                        const allReady = total > 0 && prontos >= total;
                        const liberado = p.RESULTADO_LIBERADO === 'T';
                        return (
                        <tr key={i} style={{ cursor: 'pointer' }} onClick={() => navigate(`/protocolo/${p.PROTOCOLO}`)}>
                          <td style={styles.td}>{p.LOCAL}/{p.PROTOCOLO}</td>
                          <td style={styles.td}>{p.DATA ? new Date(p.DATA).toLocaleDateString('pt-BR') : '—'}</td>
                          <td style={styles.td}>{p.LOCAL_NOME || p.LOCAL}</td>
                          <td style={styles.td}>{p.CONVENIO_NOME || '—'}</td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, backgroundColor: allReady ? '#dcfce7' : '#fef3c7', color: allReady ? '#166534' : '#92400e' }}>
                              {prontos}/{total}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, backgroundColor: liberado ? '#dcfce7' : '#f3f4f6', color: liberado ? '#166534' : '#6b7280' }}>
                              {liberado ? 'Sim' : 'Não'}
                            </span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Info do vínculo WhatsApp */}
              {sistema.vinculo && (
                <div style={styles.card}>
                  <div style={styles.cardTitle}>Vínculo WhatsApp</div>
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Paciente ID</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.vinculo.paciente_id || sistema.paciente_codigo}</div>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Confirmado</label>
                      <div style={{ fontSize: '13px', color: '#374151' }}>{sistema.vinculo?.confirmado ? 'Sim' : 'Não'}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ─── TAB: Conversas ─── */}
      {tab === 'conversas' && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={styles.cardTitle}>Histórico de mensagens</div>
            <button
              style={{ ...styles.btnSmall, background: '#059669', color: '#fff' }}
              onClick={() => navigate(`/whatsapp-conversas/${encodeURIComponent(phone)}`)}
            >
              Abrir no chat
            </button>
          </div>
          {loadingTimeline ? (
            <div style={styles.empty}>Carregando...</div>
          ) : timeline.length === 0 ? (
            <div style={styles.empty}>Nenhuma mensagem encontrada</div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {timeline.slice(0, 100).map((msg, i) => {
                const isReceived = msg.tipo === 'recebida';
                return (
                  <div
                    key={msg.id || i}
                    style={styles.msgItem}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: isReceived ? '#059669' : '#1d4ed8', marginBottom: '2px' }}>
                        {isReceived ? 'Recebida' : 'Enviada'}{msg.sender ? ` por ${msg.sender}` : ''}
                      </div>
                      <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {msg.mensagem || `[${msg.tipo_msg || 'mídia'}]`}
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                      {formatDateTime(msg.data)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── TAB: Atendimentos ─── */}
      {tab === 'atendimentos' && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Atendimentos</div>
          {loadingAtendimentos ? (
            <div style={styles.empty}>Carregando...</div>
          ) : atendimentos.length === 0 ? (
            <div style={styles.empty}>Nenhum atendimento encontrado</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Protocolo</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Aberto em</th>
                  <th style={styles.th}>Fechado em</th>
                  <th style={styles.th}>Atendente</th>
                </tr>
              </thead>
              <tbody>
                {atendimentos.map((a, i) => {
                  const statusColors = {
                    aberto: { bg: '#dcfce7', color: '#166534' },
                    fechado: { bg: '#f3f4f6', color: '#6b7280' },
                    aguardando: { bg: '#fef3c7', color: '#92400e' },
                  };
                  const sc = statusColors[a.status] || statusColors.aberto;
                  return (
                    <tr key={a.id || i}>
                      <td style={styles.td}>{a.protocolo || a.id || '—'}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color }}>
                          {a.status || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDateTime(a.aberto_em || a.created_at)}</td>
                      <td style={styles.td}>{formatDateTime(a.fechado_em)}</td>
                      <td style={styles.td}>{a.atendente_nome || a.atendente || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
